import React from "react";
import Icon from "./icons";
import { useToast } from "../../context/ToastContext";
import "./ui.css";

/** Page title block, used at the top of every routed page. */
export function PageHead({ eyebrow, title, sub, actions }) {
  return (
    <header className="pghead">
      <div className="pghead-text">
        {eyebrow && <span className="pghead-eyebrow">{eyebrow}</span>}
        <h1>{title}</h1>
        {sub && <p>{sub}</p>}
      </div>
      {actions && <div className="pghead-actions">{actions}</div>}
    </header>
  );
}

export function Panel({ title, sub, actions, children, className = "", ...rest }) {
  return (
    <section className={`panel ${className}`} {...rest}>
      {(title || actions) && (
        <div className="panel-head">
          <div>
            {title && <h2>{title}</h2>}
            {sub && <p>{sub}</p>}
          </div>
          {actions && <div className="panel-actions">{actions}</div>}
        </div>
      )}
      {children}
    </section>
  );
}

/** Labelled form control with inline validation text. */
export function Field({
  label,
  hint,
  error,
  children,
  id,
  required = false,
}) {
  return (
    <div className={`field${error ? " has-error" : ""}`}>
      <label htmlFor={id}>
        {label}
        {required && <span className="field-req" aria-hidden="true"> *</span>}
      </label>
      {children}
      {error ? (
        <p className="field-msg field-err" role="alert">
          {error}
        </p>
      ) : (
        hint && <p className="field-msg">{hint}</p>
      )}
    </div>
  );
}

export function Badge({ tone = "neutral", children, style }) {
  return (
    <span className={`badge badge-${tone}`} style={style}>
      {children}
    </span>
  );
}

/** Icon shown next to a plan's badge, keyed by plan code. */
const PLAN_ICONS = {
  free: Icon.Person,
  starter: Icon.Spark,
  standard: Icon.Bolt,
  professional: Icon.Card,
  unlimited: Icon.Shield,
  tester: Icon.Flask,
  bughunter: Icon.Bug,
  contributor: Icon.GitBranch,
};

/** Reward tiers get a persistent glow - they were earned, not bought. */
const PLAN_GLOW = new Set(["unlimited", "tester", "bughunter", "contributor"]);

/** Plan badge, coloured by the plan's own accent, with an icon and a subtle entrance. */
export function PlanBadge({ plan }) {
  if (!plan) return null;
  const accent = plan.accent || "var(--muted)";
  const code = plan.planCode || plan.code;
  const IconCmp = PLAN_ICONS[code];
  const glow = PLAN_GLOW.has(code);
  return (
    <span
      className={`badge badge-plan role-badge${glow ? " role-badge-glow" : ""}`}
      style={{
        color: accent,
        borderColor: `color-mix(in srgb, ${accent} 45%, transparent)`,
        background: `color-mix(in srgb, ${accent} 13%, transparent)`,
        "--glow": accent,
      }}
    >
      {IconCmp && <IconCmp className="role-badge-icon" />}
      {plan.badge || plan.planName || plan.name || "Free"}
    </span>
  );
}

/** Icon + label for an account role (as opposed to a paid plan). */
const ROLE_INFO = {
  user: { icon: Icon.Person, label: "User", accent: "var(--muted)" },
  staff: { icon: Icon.Headset, label: "Staff", accent: "#38bdf8" },
  admin: { icon: Icon.Shield, label: "Admin", accent: "#f87171" },
};

/** Role badge, coloured and iconed by role, with a subtle entrance animation. */
export function RoleBadge({ role }) {
  const info = ROLE_INFO[role];
  if (!info || role === "user") return null;
  const IconCmp = info.icon;
  return (
    <span
      className="badge badge-plan role-badge role-badge-glow"
      style={{
        color: info.accent,
        borderColor: `color-mix(in srgb, ${info.accent} 45%, transparent)`,
        background: `color-mix(in srgb, ${info.accent} 13%, transparent)`,
        "--glow": info.accent,
      }}
    >
      <IconCmp className="role-badge-icon" />
      {info.label.toUpperCase()}
    </span>
  );
}

export function Empty({ icon, title, sub, action }) {
  return (
    <div className="empty">
      {icon && <div className="empty-icon">{icon}</div>}
      <p className="empty-title">{title}</p>
      {sub && <p className="empty-sub">{sub}</p>}
      {action}
    </div>
  );
}

/**
 * Shown when a page cannot load its data. Distinct from Empty: this is a
 * failure the user can retry, not an absence of content.
 */
export function LoadError({ error, onRetry, what = "this page" }) {
  const offline = error?.code === "bad_gateway" || error?.status === 502;
  return (
    <div className="loaderr" role="alert">
      <div className="loaderr-icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M12 3.2 2.6 19.4h18.8z" />
          <path d="M12 10v4M12 16.6v.1" />
        </svg>
      </div>
      <p className="loaderr-title">
        {offline ? "Cannot reach the server" : `Could not load ${what}`}
      </p>
      <p className="loaderr-sub">
        {error?.message || "Something went wrong."}
      </p>
      {onRetry && (
        <button type="button" className="btn btn-primary" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

export function Spinner({ label }) {
  return (
    <div className="loading" role="status">
      <span className="spinner" aria-hidden="true" />
      {label || "Loading"}
    </div>
  );
}

export function Stat({ label, value, tone, icon: IconGlyph }) {
  return (
    <div className={`stat${tone ? ` stat-${tone}` : ""}`}>
      {IconGlyph && (
        <span className="stat-icon" aria-hidden="true">
          <IconGlyph />
        </span>
      )}
      <strong>{value}</strong>
      <span className="stat-label">{label}</span>
    </div>
  );
}

/** Confirm-then-act button, for destructive rows. */
export function DangerButton({ onConfirm, children, confirmLabel = "Sure?" }) {
  const [armed, setArmed] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const toast = useToast();

  React.useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 3000);
    return () => clearTimeout(t);
  }, [armed]);

  async function fire() {
    setBusy(true);
    try {
      await onConfirm();
    } catch (err) {
      // Every call site previously had no error handling at all here - a
      // failed confirm (a coupon that can't be deleted, a settle that's
      // already settled, etc.) surfaced only as an unhandled rejection in
      // the console, never as anything the person clicking the button saw.
      toast.err(err?.message || "That didn't work.");
    } finally {
      setBusy(false);
      setArmed(false);
    }
  }

  return (
    <button
      type="button"
      className={`btn btn-sm${armed ? " btn-danger" : ""}`}
      onClick={() => (armed ? fire() : setArmed(true))}
      disabled={busy}
    >
      {busy && <span className="spinner" aria-hidden="true" />}
      {armed ? confirmLabel : children}
    </button>
  );
}

/** Relative time that stays readable without pulling in a date library. */
export function timeAgo(ts) {
  if (!ts) return "—";
  const diff = Date.now() - ts;
  const abs = Math.abs(diff);
  const units = [
    [1000, "s"],
    [60_000, "m"],
    [3_600_000, "h"],
    [86_400_000, "d"],
  ];
  if (abs < 1000) return "just now";
  if (abs < 60_000) return `${Math.floor(abs / 1000)}s ${diff > 0 ? "ago" : "left"}`;
  if (abs < 3_600_000) return `${Math.floor(abs / 60_000)}m ${diff > 0 ? "ago" : "left"}`;
  if (abs < 86_400_000) return `${Math.floor(abs / 3_600_000)}h ${diff > 0 ? "ago" : "left"}`;
  void units;
  return new Date(ts).toLocaleDateString();
}

export function money(n) {
  return `$${Number(n || 0).toFixed(2).replace(/\.00$/, "")}`;
}
